import { formatUnits } from '@ethersproject/units';
import Skeleton from 'react-loading-skeleton';
import { Network, StringUtils } from '@manifoldxyz/js-ts-utils';
import { TELEMETRY_EVENT_MAP, trackedGalleryContractLink } from '@/common/telemetry';
import AccordionSection from '@/components/AccordionSection';
import ExternalLinkSection from '@/components/ExternalLinkSection';
import { useClaim } from '@/hooks/mainStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import useMainStore from '@/store/mainStore';
import Leaderboard from './Leaderboard';

export default function InfoSections() {
  const studioInstance = useMainStore((state) => state.studioInstance);
  const analytics = useAnalytics();
  const { claim: claimOnChain } = useClaim();
  const walletAddress = useMainStore((state) => state.walletAddress);

  if (!studioInstance) {
    return (
      <div className="m-claim--flex m-claim--flex-col m-claim--gap-4">
        <Skeleton height={52} />
        <Skeleton height={52} />
        <Skeleton height={52} />
      </div>
    );
  }

  let formattedMintPriceString = 'Free';
  if (
    studioInstance?.publicData.mintPrice.value &&
    studioInstance?.publicData.mintPrice.value !== '0'
  ) {
    formattedMintPriceString =
      formatUnits(
        studioInstance?.publicData.mintPrice.value,
        studioInstance?.publicData.mintPrice.decimals,
      ) +
      ' ' +
      studioInstance?.publicData.mintPrice.symbol;
  }

  return (
    <div className="m-claim--flex m-claim--flex-col">
      {/* Leaderboard */}
      {studioInstance && claimOnChain && claimOnChain.total > 0 && (
        <Leaderboard
          id={studioInstance.id}
          walletAddress={walletAddress}
        />
      )}

      <AccordionSection title="VIEW DETAILS">
        <div className="m-claim--flex m-claim--flex-col m-claim--bg-[#101010]">
          <div className="m-claim--grid m-claim--grid-cols-2 m-claim--gap-4 m-claim--p-4">
            {/* Column 1 */}
            <div className="m-claim--flex m-claim--flex-col m-claim--gap-6">
              {/* Token Standard */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Token Spec
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm m-claim--uppercase">
                  {studioInstance?.publicData.contract.spec}
                </div>
              </div>

              {/* Start Date */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Start Date
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm">
                  {studioInstance?.publicData.startDate
                    ? new Date(studioInstance.publicData.startDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })
                    : 'STARTED'}
                </div>
              </div>

              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  End Date
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm">
                  {studioInstance?.publicData.endDate
                    ? new Date(studioInstance.publicData.endDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                      })
                    : 'NEVER'}
                </div>
              </div>

              {/* Edition Limit */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Edition Limit
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm">
                  {claimOnChain?.totalMax === 0 || !claimOnChain?.totalMax
                    ? 'Unlimited'
                    : claimOnChain?.totalMax}
                </div>
              </div>
            </div>

            {/* Column 2 */}
            <div className="m-claim--flex m-claim--flex-col m-claim--gap-6">
              {/* Claim Network */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Network
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm m-claim--uppercase">
                  {Network.getDisplayNameForNetworkId(studioInstance?.publicData.network)}
                </div>
              </div>

              {/* Redeem Contract */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Contract
                </div>
                <a
                  href={trackedGalleryContractLink(
                    studioInstance?.publicData.network,
                    studioInstance?.publicData.contract.contractAddress,
                    {
                      medium: 'claim',
                      campaign: 'info_section',
                    },
                  )}
                  className="m-claim--flex m-claim--items-center m-claim--font-semimono m-claim--text-sm m-claim--transition-colors hover:m-claim--text-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View redeem contract"
                  tabIndex={0}
                >
                  {StringUtils.abbreviateAddress(
                    studioInstance?.publicData.contract.contractAddress,
                  )}
                  <svg
                    className="m-claim--ml-1 m-claim--size-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </a>
              </div>
              {/* Original Mint Price */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Original Price
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm">
                  {formattedMintPriceString}
                </div>
              </div>

              {/* Minting Restriction */}
              <div className="m-claim--flex m-claim--flex-col">
                <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
                  Minting Restriction
                </div>
                <div className="m-claim--font-semimono m-claim--text-sm">
                  {studioInstance?.publicData.mintingRestriction === 'None' ||
                  studioInstance?.publicData.mintingRestriction === undefined
                    ? 'None'
                    : 'Gated'}
                </div>
              </div>
            </div>
          </div>

          {/* Original Media - Full Width */}
          <div className="m-claim--border-t m-claim--border-default m-claim--p-4">
            <div className="m-claim--mb-1 m-claim--text-xs m-claim--uppercase m-claim--text-secondary">
              Original Media
            </div>
            <a
              href={
                studioInstance?.publicData.asset.animation_url ||
                studioInstance?.publicData.asset.image
              }
              className="m-claim--flex m-claim--items-center m-claim--font-semimono m-claim--text-sm m-claim--transition-colors hover:m-claim--text-secondary"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open original media in new tab"
              tabIndex={0}
            >
              View in new tab
              <svg
                className="m-claim--ml-1 m-claim--size-3"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 6H6C4.89543 6 4 6.89543 4 8V18C4 19.1046 4.89543 20 6 20H16C17.1046 20 18 19.1046 18 18V14M14 4H20M20 4V10M20 4L10 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </AccordionSection>
      <hr />
      <ExternalLinkSection
        className="m-claim--my-4"
        title="View On Manifold Gallery"
        onClick={() => {
          analytics.capture({
            event: TELEMETRY_EVENT_MAP.VIEW_ON_MANIFOLD_BUTTON_CLICK,
          });
          window.open(
            trackedGalleryContractLink(
              studioInstance?.publicData.network,
              studioInstance?.publicData.contract.contractAddress,
              {
                medium: 'claim',
                campaign: 'info_section',
              },
            ),
            '_blank',
            'noopener,noreferrer',
          );
        }}
      />
      <hr />
    </div>
  );
}
